import React, { PureComponent, Children, isValidElement, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MenuContext from './MenuContext';

function diff(a, b, propNames) {
  let result = false;
  propNames.forEach(name => {
    if (a[name] !== b[name]) {
      result = true;
    }
  });
  return result;
}
function getMenuItems(children) {
  const items = [];
  Children.forEach(children, item => {
    if (isValidElement(item)) {
      items.push(item);
    }
  });
  return items;
}

function getValue(value) {
  if (Array.isArray) {
    return value;
  }
  if (value != null) {
    return [value];
  }
  return [];
}
/**
 *  Menu
 */
class Menu extends PureComponent {
  // TODO: 存在性能问题
  static propTypes = {
    /**
     * 类前缀
     * */
    prefixCls: PropTypes.string,
    /** 是否自动折叠 */
    autoFold: PropTypes.bool,
    /** 是否可选中 */
    selectable: PropTypes.bool,
    /** 是否可以多选 */
    multiple: PropTypes.bool,
    /** 默认值 */
    defaultValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
    /**
     * 当前值，适用于受控模式下使用
     */
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
    ]),
    /** 是否自动缩进 */
    autoIndent: PropTypes.bool,
    /** 缩进长度（单位px） */
    indent: PropTypes.number,
    /** 值改变时回调 function(e, value) */
    onChange: PropTypes.func,
    /** 选择菜单中一项时回调 function(e, value) */
    onSelect: PropTypes.func,
    /** 当前菜单层数（计算缩进使用） */
    level: PropTypes.number,
    /** 同原生role属性 */
    role: PropTypes.string,
    /** 子菜单模式（内联，弹出，多级） */
    mode: PropTypes.oneOf(['inline', 'popup', 'parallel']),
    /** 尺寸 */
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    /** 是否为紧凑模式 */
    compact: PropTypes.bool,
  };

  static defaultProps = {
    defaultValue: [],
    prefixCls: 'ne-menu',
    selectable: false,
    autoFold: false,
    autoIndent: true,
    multiple: false,
    level: 1,
    indent: 14,
    role: 'menu',
    mode: 'inline',
    size: 'medium',
    compact: false,
    indentBase: 8,
  };

  static getDerivedStateFromProps(props, state) {
    const propNames = [
      'autoFold',
      'multiple',
      'selectable',
      'autoIndent',
      'mode',
      'size',
      'indentBase',
    ];
    const {
      autoFold,
      multiple,
      selectable,
      autoIndent,
      indent,
      value,
      mode,
      defaultValue,
      size,
      indentBase,
    } = props;
    if (state.isControlled) {
      propNames.push('value');
    }
    if (diff(props, state.store, propNames) || defaultValue !== state.prevDefaultValue) {
      return {
        prevDefaultValue: defaultValue,
        store: {
          autoFold,
          multiple,
          selectable,
          autoIndent,
          indent,
          value: state.isControlled ? value : getValue(defaultValue),
          onSelect: state.store.onSelect,
          size,
          indentBase,
          mode,
        },
      };
    }

    return null;
  }

  static displayName = 'NeatUI.Menu';

  constructor(props, context) {
    super(props, context);
    const {
      defaultValue,
      multiple,
      value,
      selectable,
      autoFold,
      autoIndent,
      indent,
      mode,
      size,
      indentBase,
    } = props;
    this.isControlled = value != null;
    const menuValue = this.isControlled ? value : getValue(defaultValue);
    this.state = {
      prevDefaultValue: defaultValue,
      isControlled: this.isControlled,
      store: {
        autoFold,
        multiple,
        selectable,
        autoIndent,
        indent,
        indentBase,
        value: menuValue,
        onSelect: this.onSelect,
        size,
        mode,
      },
    };
  }

  updateStore = newValue => {
    this.setState(state => ({
      store: Object.assign({}, state.store, newValue),
    }));
  };

  onSelect = (e, itemValue) => {
    const { onSelect, onChange, selectable, multiple, value: propsValue } = this.props;
    const { store } = this.state;
    let value = selectable ? (this.isControlled ? propsValue : store.value) : [];
    value = Array.isArray(value) ? value : [value];
    onSelect && onSelect(e, itemValue);
    if (selectable) {
      if (multiple) {
        const itemIndex = value.indexOf(itemValue);
        value = [...value];
        if (itemIndex === -1) {
          value.push(itemValue);
        } else {
          value.splice(itemIndex, 1);
        }
        if (!this.isControlled) {
          this.updateStore({
            value,
          });
        }
      } else if (!this.isControlled) {
        this.updateStore({
          value: [itemValue],
        });
      }
    }
    if ((selectable && !value.includes(itemValue)) || multiple) {
      onChange && onChange(e, multiple ? value : itemValue, itemValue);
    }
  };

  render() {
    const {
      children,
      prefixCls,
      className,
      selectable,
      autoFold,
      disableAutoIndent,
      value,
      defaultValue,
      onSelect,
      onChange,
      level,
      autoIndent,
      indent,
      compact,
      size,
      indentBase,
      ...other
    } = this.props;
    const { store } = this.state;
    const classes = classNames(
      prefixCls,
      {
        [`${prefixCls}-compact`]: compact,
        [`${prefixCls}-size-${size}`]: size,
      },
      className,
    );
    const menu = getMenuItems(children).map((item, key) => {
      if (!React.isValidElement(item)) {
        return null;
      }
      const newProps = {
        level,
        size,
        key: item.props.value != null ? item.props.value : `menu-${key}`,
      };
      if (compact) {
        newProps.compact = true;
      }
      if (item.type.isSubMenu && compact) {
        newProps.mode = 'popup';
        newProps.trigger = 'click';
      }
      return cloneElement(item, newProps);
    });
    return (
      <MenuContext.Provider value={store}>
        <ul className={classes} {...other}>
          {menu}
        </ul>
      </MenuContext.Provider>
    );
  }
}

export default Menu;



// WEBPACK FOOTER //
// ./src/menu/Menu.jsx